import styled, { SystemProp } from '@xstyled/styled-components';
import CSS from 'csstype';
import React, { FC } from 'react';
import { createPortal } from 'react-dom';
import { animated, useTransition } from '@react-spring/web';
import { AppTheme } from '~/shell/theme';
import { Flex } from '../flex/Flex';

const Container = animated(styled.div`
  min-width: 200px;
  background: ${p => p.theme.colors.white};
  border-radius: ${p => p.theme.radii.md};
`);

export const Modal: FC<{
  open: boolean;
  horizontal?: SystemProp<CSS.Property.JustifyContent, AppTheme>;
  vertical?: SystemProp<CSS.Property.AlignItems, AppTheme>;
  width?: string;
  height?: string;
  minHeight?: string;
  padding?: string;
  borderRadius?: string;
  role?: React.AriaRole;
  'aria-modal'?: boolean | 'true' | 'false';
  'aria-labelledby'?: string;
  'aria-label'?: string;
}> = ({
  open,
  children,
  horizontal,
  vertical,
  width,
  height,
  minHeight,
  padding,
  borderRadius,
  role,
  'aria-modal': ariaModal,
  'aria-labelledby': ariaLabelledby,
  'aria-label': ariaLabel
}) => {
  const transition = useTransition(open, {
    from: { opacity: 0, transform: 'translateY(400px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    leave: { opacity: 0, transform: 'translateY(-400px)' }
  });

  const AdditionalStyle: { [key: string]: string | number } = {};

  if (width) {
    AdditionalStyle['width'] = width;
  }

  if (height) {
    AdditionalStyle['height'] = height;
  }

  if (minHeight) {
    AdditionalStyle['minHeight'] = minHeight;
  }

  if (padding) {
    AdditionalStyle['padding'] = padding;
  }

  if (borderRadius) {
    AdditionalStyle['borderRadius'] = borderRadius;
  }

  return createPortal(
    <Flex
      variant="center"
      position="fixed"
      zIndex="dialogs"
      justifyContent={horizontal}
      alignItems={vertical}
      top={0}
      p="lg"
    >
      {transition(
        (style, item) =>
          item && (
            <Container
              role={role}
              aria-modal={ariaModal}
              aria-labelledby={ariaLabelledby}
              aria-label={ariaLabel}
              style={{ ...style, ...AdditionalStyle }}
              onClick={(e: MouseEvent) => e.stopPropagation()}
            >
              {children}
            </Container>
          )
      )}
    </Flex>,
    document.body
  );
};
